// @flow

import { v4 } from 'uuid';
import { FormattedMessage } from 'react-intl';
import React, { Fragment } from 'react';
import type { Node } from 'react';
import { Button } from '@riseart/common';
import { Section, SectionContent, Wrapper } from '@riseart/layout';
import { GridSquare, GridSquareItem } from '@riseart/grid';
import { useQuery } from 'shared_services/apollo/useQuery';
import { Boxes } from 'shared_components/common/boxes/Boxes';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import FILTER_ARTICLE_CATEGORIES from 'shared_data/queries/articles/filterCategories.graphql';

import {
  blogArticleActionWrapperCls,
  blogArticleBtnPlaceholderCls,
} from 'shared_components/pages/blog/Article.less';

const preloaderId = v4();

/**
 * MoreCategoriesSection
 *
 * @returns {Node}
 */
export function MoreCategoriesSection(): Node {
  const { loading, data, error } = useQuery(FILTER_ARTICLE_CATEGORIES, {
    ssr: false,
    variables: {
      inputArticleCategoryFilter: {
        items: 4,
        page: 1,
      },
    },
  });
  const { filterArticleCategories: { items: articleCategories = [] } = {} } = data || {};

  if (error || (!loading && (!articleCategories || !articleCategories.length))) {
    return null;
  }

  if (loading) {
    return (
      <Fragment>
        <DataProviderPreloaderPage uniqueId={preloaderId} attach={loading} />
        <Section>
          <Wrapper className={blogArticleActionWrapperCls}>
            <SectionContent
              title={<FormattedMessage id="components.blog.moreFrom" />}
              headingProps={{ align: 'center', level: '2' }}
            >
              <GridSquare justify="space-between">
                {Array.apply(0, Array(4)).map((item, idx) => (
                  <GridSquareItem key={idx} columnCount={4}>
                    <div className={blogArticleBtnPlaceholderCls} />
                  </GridSquareItem>
                ))}
              </GridSquare>
            </SectionContent>
          </Wrapper>
        </Section>
      </Fragment>
    );
  }

  return (
    <Section>
      <Wrapper className={blogArticleActionWrapperCls}>
        <SectionContent
          title={<FormattedMessage id="components.blog.moreFrom" />}
          headingProps={{ align: 'center', level: '2' }}
        >
          <Boxes>
            {articleCategories.map(({ id, name, uri, type }) => (
              <Button
                key={id}
                isAnchor
                type="secondary"
                width="container"
                to={UrlAssembler.articleCategory(uri, type)}
              >
                {name}
              </Button>
            ))}
          </Boxes>
        </SectionContent>
      </Wrapper>
    </Section>
  );
}
